import { CSSProperties, ReactNode } from 'react';

/**
 * A patient's name / file number in a list — as a link to their card in clinicui,
 * or as plain text.
 *
 * Why (Ohad, 2026-09-22): people who may not open patients inside clinicui were
 * reaching patient cards THROUGH our lists, because every list deep-links to
 * `clinicui.taliaz-cm.com/patient?id=`. clinicui does not block them on arrival, so
 * our link was the whole journey. The `patient_card` grant decides who keeps it.
 *
 * ⚠ This reduces exposure, it does not close the hole — the file number is still on
 * screen and the URL pattern is guessable. It turns one accidental click into a
 * deliberate act. The real fix belongs in clinicui.
 *
 * `linked` is passed IN rather than read here: `usePermissions()` fetches on mount,
 * and this renders once per row — reading it inside would be one request per patient.
 */
export function PatientCardLink({
  linked, href, title, style, className, children,
}: {
  linked: boolean;
  href: string;
  title?: string;
  style?: CSSProperties;
  className?: string;
  children: ReactNode;
}) {
  // No tooltip explaining the absence: the row simply reads as data, which is what
  // it is for someone without the grant.
  if (!linked) return <span className={className} style={style}>{children}</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
       className={className} style={style} title={title}>
      {children}
    </a>
  );
}
